import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { BREAKPOINTS } from "@/settings";
import { ComponentProps, ReactNode } from "react";
import { useMediaQuery } from "usehooks-ts";

import { ActiveSquadSection } from "./ActiveSquadSection";

/*
  On tablets the panel's row sits under the pitch, so the pitch gives up
  height to keep both on screen; from touch-device up it sits beside it. On
  phones the page scrolls anyway, so the pitch is a full-width square, up to
  a size where it would start to feel oversized.
*/
const PITCH_LIMITS =
  "max-w-150 mobile:max-w-[max(16rem,min(50rem,calc(100dvh-31rem)))] touch-device:max-w-[min(50rem,calc(100dvh-11rem))]";

/**
 * My Club's page layout: the squad on its pitch, and a panel of cards about
 * it. On desktop the panel hugs the squad on the right, as tall as the squad
 * column, scrolling inside; on tablets and phones it is a row under the
 * pitch that scrolls sideways. The club home and the recommendations page
 * share it, so both read the same way.
 */
export const SquadWithPanel = ({
  squad,
  header,
  children,
  panelId,
  panelFirstOnPhone = false,
  panelHighlighted = false,
  fitCards = false,
}: {
  squad: ComponentProps<typeof ActiveSquadSection>;
  /** Above the cards: heading, actions, notices. */
  header: ReactNode;
  /** The cards; each is one item in the list or row. */
  children: ReactNode;
  panelId?: string;
  /** On phones the cards come before the squad, for pages led by the cards. */
  panelFirstOnPhone?: boolean;
  /** Rings the panel, to point the user at it for a moment. */
  panelHighlighted?: boolean;
  /**
   * In the sideways row (tablets, phones), each card as wide as its content
   * rather than a set width, for cards whose figures must not be cut off.
   */
  fitCards?: boolean;
}) => {
  /* Up to 1024px, matching the touch-device: classes (1025px and up) below;
     TOUCH_DEVICE.query also matches 1025px, where they already sit side by
     side. */
  const isTouch = useMediaQuery(
    `(max-width: ${BREAKPOINTS.TOUCH_DEVICE.value}px)`,
  );

  return (
    <div className="flex flex-col gap-6 touch-device:flex-row touch-device:items-stretch">
      {/* The squad column is exactly as wide as its pitch, so the panel sits
          right beside the squad rather than across a gap. */}
      <ActiveSquadSection
        pitchClassName={PITCH_LIMITS}
        className="touch-device:w-[min(50rem,calc(100dvh-11rem),calc(100%-25.5rem))] touch-device:flex-none"
        {...squad}
      />

      <section
        id={panelId}
        className={cn(
          "flex min-h-0 min-w-0 scroll-mt-24 flex-col gap-3 touch-device:w-96 touch-device:flex-none",
          // An outline, so the ring never moves the cards.
          "rounded-2xl outline-2 outline-offset-8 outline-transparent transition-[outline-color] duration-700",
          panelHighlighted && "outline-blue-400/70",
          panelFirstOnPhone && "max-mobile:order-first",
        )}
      >
        {header}

        {isTouch ? (
          <ScrollArea enableShadows shadowDirection="x">
            <div
              className={cn(
                "flex gap-3 pb-3 [&>*]:flex-none",
                fitCards
                  ? "[&>*]:w-max [&>*]:max-w-[calc(100vw-2rem)]"
                  : "[&>*]:w-[min(22rem,85vw)]",
              )}
            >
              {children}
            </div>
            <ScrollBar orientation="horizontal" />
          </ScrollArea>
        ) : (
          <div className="relative min-h-0 flex-1">
            <ScrollArea
              className="absolute inset-0"
              enableShadows
              shadowDirection="y"
            >
              <div className="flex flex-col gap-2.5 pr-3">{children}</div>
            </ScrollArea>
          </div>
        )}
      </section>
    </div>
  );
};
